import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | Masonry Pro</title>
        <meta name="description" content="Privacy policy for Masonry Pro Construction. Learn how we collect, use, and safeguard your personal information when you visit our site or request a quote." />
        <meta property="og:description" content="Privacy policy for Masonry Pro Construction. Learn how we collect, use, and safeguard your personal information when you visit our site or request a quote." />
        <meta property="og:title" content="Privacy Policy | Masonry Pro" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        <main className="py-16 md:py-20">
          <div className="container mx-auto px-4 max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-muted-foreground mb-6">Effective Date: February 11, 2026</p>
            <Button variant="gold" size="lg" className="mb-10" asChild>
              <Link to="/data-request">Opt Out of Data Sharing</Link>
            </Button>

            <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
              <p>
                Masonry Pro Construction ("Company," "we," "us," or "our") operates the website located at masonry-pro.com (the "Site"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our Site, request an estimate, or otherwise interact with our business. Please read this policy carefully. If you do not agree with the terms of this Privacy Policy, please do not access the Site.
              </p>
              <p>
                We reserve the right to make changes to this Privacy Policy at any time and for any reason. We will alert you about any changes by updating the Effective Date of this Privacy Policy. You are encouraged to periodically review this Privacy Policy to stay informed of updates.
              </p>

              <h2 className="text-2xl font-bold text-foreground">1. Information We Collect</h2>

              <h3 className="text-xl font-semibold text-foreground">1.1 Personal Information You Provide</h3>
              <p>We may collect personal information that you voluntarily provide to us when you:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Fill out a contact form or request a free estimate on our Site</li>
                <li>Call, email, or text us directly</li>
                <li>Subscribe to a newsletter or marketing communications</li>
                <li>Leave a review or testimonial</li>
                <li>Communicate with us through social media platforms</li>
              </ul>
              <p>This information may include:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Full name</li>
                <li>Mailing address and/or property address</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Project details and descriptions of work requested</li>
                <li>Photos of your property that you submit to us</li>
                <li>Any other information you choose to provide</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground">1.2 Information Collected Automatically</h3>
              <p>When you visit our Site, we may automatically collect certain information about your device and your visit, including:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>IP address and approximate geographic location</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Referring website or search engine and search terms used</li>
                <li>Pages visited and time spent on each page</li>
                <li>Date and time of your visit</li>
                <li>Device type (desktop, mobile, tablet)</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground">1.3 Cookies and Tracking Technologies</h3>
              <p>
                We may use cookies, web beacons, pixel tags, and similar tracking technologies to collect information about your browsing activities on our Site. Cookies are small data files stored on your device. You can set your browser to refuse all or some cookies, or to alert you when cookies are being sent. If you disable or refuse cookies, some parts of our Site may not function properly.
              </p>
              <p>We may use the following types of cookies:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Strictly Necessary Cookies:</strong> Required for the Site to function (e.g., session management, security).</li>
                <li><strong>Analytics Cookies:</strong> Help us understand how visitors use the Site (e.g., Google Analytics).</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground">1.4 Google Analytics</h3>
              <p>
                We use Google Analytics, a web analytics service provided by Google LLC. Google Analytics uses cookies to help us analyze how users interact with the Site. The information generated by the cookie about your use of the Site is transmitted to and stored by Google on servers in the United States.
              </p>

              <h2 className="text-2xl font-bold text-foreground">2. How We Use Your Information</h2>
              <p>We use the information we collect for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>To respond to your inquiries and provide estimates for masonry, concrete, paving, and waterproofing services</li>
                <li>To communicate with you about projects, scheduling, and service-related matters</li>
                <li>To process and manage service agreements</li>
                <li>To improve our Site, services, and customer experience</li>
                <li>To send you marketing communications (only with your consent, where required by law)</li>
                <li>To comply with legal obligations and protect our legal rights</li>
                <li>To analyze website traffic and usage patterns</li>
                <li>To detect, prevent, and address fraud or technical issues</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground">3. How We Share Your Information</h2>
              <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Service Providers:</strong> We may share information with third-party vendors who perform services on our behalf, such as website hosting, email delivery, analytics, and advertising.</li>
                <li><strong>Legal Requirements:</strong> We may disclose your information if required by law, regulation, legal process, or governmental request.</li>
                <li><strong>Business Transfers:</strong> If we are involved in a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction.</li>
                <li><strong>Protection of Rights:</strong> We may disclose information to protect and defend the rights, property, or safety of our company, our customers, or others.</li>
                <li><strong>With Your Consent:</strong> We may share your information for any other purpose with your affirmative consent.</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground">4. Third-Party Links and Services</h2>
              <p>
                Our Site may contain links to third-party websites, services, or applications that are not operated by us. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.
              </p>

              <h2 className="text-2xl font-bold text-foreground">5. Data Retention</h2>
              <p>We retain your personal information only for as long as reasonably necessary to fulfill the purposes for which it was collected. For example:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Contact form submissions:</strong> Retained for up to 3 years after your last interaction with us.</li>
                <li><strong>Project records:</strong> Retained for a minimum of 6 years after project completion.</li>
                <li><strong>Website analytics data:</strong> Retained in accordance with Google Analytics default data retention settings.</li>
                <li><strong>Marketing communications records:</strong> Retained until you unsubscribe or request deletion.</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground">6. Data Security</h2>
              <p>
                We use commercially reasonable administrative, technical, and physical security measures to protect your personal information from unauthorized access, use, alteration, or destruction. However, no method of transmission over the Internet or method of electronic storage is 100% secure.
              </p>

              <h2 className="text-2xl font-bold text-foreground">7. Your Privacy Rights</h2>

              <h3 className="text-xl font-semibold text-foreground">7.1 New Jersey Privacy Rights</h3>
              <p>Effective January 15, 2025, the New Jersey Data Privacy Act grants New Jersey residents specific rights regarding their personal data. As a New Jersey resident, you have the right to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Right to Know/Access:</strong> Confirm whether we are processing your personal data and access that data.</li>
                <li><strong>Right to Correct:</strong> Request correction of inaccuracies in your personal data.</li>
                <li><strong>Right to Delete:</strong> Request deletion of personal data you have provided to us.</li>
                <li><strong>Right to Data Portability:</strong> Obtain a copy of your personal data in a portable, readily usable format.</li>
                <li><strong>Right to Opt Out:</strong> Opt out of the processing of your personal data for purposes of targeted advertising, sale of personal data, or profiling.</li>
              </ul>
              <p>
                To exercise any of these rights, please contact us using the information provided below. We will respond to your request within 45 days.
              </p>

              <h3 className="text-xl font-semibold text-foreground">7.2 Additional State Privacy Rights</h3>
              <p>
                If you are a resident of a state with applicable consumer privacy legislation (such as California under the CCPA/CPRA, Virginia, Colorado, Connecticut, or others), you may have additional or similar rights under your state's law. Please contact us to exercise those rights.
              </p>

              <h3 className="text-xl font-semibold text-foreground">7.3 Do Not Track Signals</h3>
              <p>
                Our Site does not respond to browser Do Not Track (DNT) signals. However, we do honor Global Privacy Control (GPC) signals as required by the New Jersey Data Privacy Act. If your browser transmits a GPC signal, we will treat it as an opt-out of data processing for targeted advertising purposes.
              </p>

              <h2 className="text-2xl font-bold text-foreground">8. Children's Privacy</h2>
              <p>
                Our Site and services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children under 18.
              </p>

              <h2 className="text-2xl font-bold text-foreground">9. Marketing Communications</h2>
              <p>If you have opted in to receive marketing communications from us, you may opt out at any time by:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Clicking the "unsubscribe" link in any marketing email</li>
                <li>Contacting us directly using the information below</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground">10. SMS/Text Message Policy</h2>
              <p>
                If you provide your phone number and consent to receive text messages from us, we may send you messages related to appointment scheduling, estimate follow-ups, project updates, or promotional offers.
              </p>

              <h2 className="text-2xl font-bold text-foreground">11. Updates to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. When we make material changes, we will update the Effective Date at the top of this page. Your continued use of the Site after any changes constitutes your acceptance of the revised policy.
              </p>

              <h2 id="contact-privacy" className="text-2xl font-bold text-foreground">12. Contact Us</h2>
              <p>If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:</p>
              <p>
                <strong>Masonry Pro Construction</strong><br />
                36 Highview Ave, Totowa, NJ 07512<br />
                Email: <a href="mailto:masonryproconstruction@aol.com" className="text-secondary hover:underline">masonryproconstruction@aol.com</a><br />
                Phone: <a href="tel:+19737252395" className="text-secondary hover:underline">(973) 725-2395</a>
              </p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}
